import { useState, useRef, useEffect, useMemo } from "react";
import styles from "./CustomMultiSelect.module.css";
import { cx } from "../../../Util/classHelpers";

const CustomMultiSelect = ({ 
  options, 
  onSelect, 
  selected = [], 
  selectClassName, 
  optionClassName, 
  selectedOptionClassName, 
  wrapperClassName, 
  dropdownClassName,
  label = null, 
  AllLabel = null
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    if (!onSelect) return;
    
    if (option.value === "all") {
      const allIndividualValues = options.map(opt => opt.value);
      const allSelected = allIndividualValues.every(value => selected.includes(value));
      
      if (allSelected) {
        onSelect([]);
      } else {
        onSelect(allIndividualValues);
      }
    } else {
      const isSelected = selected.includes(option.value);
      let newSelected;
      
      if (isSelected) {
        newSelected = selected.filter(value => value !== option.value && value !== "all");
      } else {
        const updatedSelected = [...selected.filter(value => value !== "all"), option.value];
        
        const allIndividualValues = options.map(opt => opt.value);
        const allIndividualSelected = allIndividualValues.every(value => updatedSelected.includes(value));
        
        if (allIndividualSelected && AllLabel) {
          newSelected = ["all", ...updatedSelected];
        } else {
          newSelected = updatedSelected;
        }
      }
      
      onSelect(newSelected);
    }
  };

  const isOptionSelected = (optionValue) => {
    if (optionValue === "all") {
      const allIndividualValues = options.map(opt => opt.value);
      return allIndividualValues.every(value => selected.includes(value));
    }
    return selected.includes(optionValue);
  };

  const allOptions = useMemo(() => {
    if (AllLabel) {
      return [{ value: "all", label: AllLabel }, ...options];
    }
    return options;
  }, [options, AllLabel]);

  const isPartiallySelected = useMemo(() => {
    if (!selected || selected.length === 0) return false;
    
    const individualOptions = options.map(opt => opt.value);
    const selectedIndividualOptions = selected.filter(val => val !== "all");
    
    return selectedIndividualOptions.length > 0 && selectedIndividualOptions.length < individualOptions.length;
  }, [selected, options]);

  return (
    <div ref={ref} className={cx(styles.selectWrapper, wrapperClassName)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cx(
          styles.selectButton, 
          selectClassName
        )}
        data-options-open={open}
        data-selected={selected.length > 0}
        data-partially-selected={isPartiallySelected}
      >
        <span className={styles.selectLabel}>
          {label}
        </span>
        <span className={styles.selectArrow}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 8L10 12L14 8"
              stroke="#4A4A4A"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      {open && (
        <div className={cx(styles.selectDropdown, dropdownClassName)}>
          {allOptions.map((option, index) => (
            <div
              key={option.value}
              onClick={() => handleSelect(option)}
              className={cx(
                styles.selectOption,
                isOptionSelected(option.value) && styles.selectOptionSelected,
                optionClassName,
                isOptionSelected(option.value) && selectedOptionClassName,
                AllLabel && index === 0 && styles.selectOptionWithBorder
              )}
            >
              <div className={styles.checkboxContainer}>
                <div 
                  className={cx(
                    styles.checkbox,
                    isOptionSelected(option.value) && styles.checkboxSelected
                  )}
                  data-state={isOptionSelected(option.value) ? "On" : "Off"}
                >
                  {isOptionSelected(option.value) && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={styles.checkboxTick}
                    >
                      <path
                        d="M7.16147 11.6366L13.6403 5.15777C13.7518 5.04614 13.8823 4.98914 14.0318 4.98677C14.1813 4.98439 14.3143 5.04139 14.4307 5.15777C14.547 5.27414 14.6052 5.40777 14.6052 5.55864C14.6052 5.70964 14.547 5.84333 14.4307 5.9597L7.63603 12.766C7.5004 12.9015 7.34222 12.9692 7.16147 12.9692C6.98072 12.9692 6.82253 12.9015 6.6869 12.766L3.55565 9.6347C3.44415 9.52308 3.38915 9.39058 3.39065 9.2372C3.39203 9.08395 3.4509 8.94914 3.56728 8.83277C3.68365 8.71639 3.81728 8.6582 3.96815 8.6582C4.11915 8.6582 4.25284 8.71639 4.36922 8.83277L7.16147 11.6366Z"
                        fill="white"
                      />
                    </svg>
                  )}
                </div>
                <div className={styles.optionLabel}>
                  {option.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomMultiSelect;
